import {
    AbstractRecommendedNotificationParams,
    isVespaRecommendedNotificationParams,
} from '@pushly/models/lib/structs/journeys'
import { InvalidFieldException } from '@pushly/models/lib/exceptions/invalid-field-exception'
import { MissingFieldException } from '@pushly/models/lib/exceptions/missing-field-exception'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { extractFriendlyErrorMessage } from '../../../../exceptions/helpers'
import { FEAT_INTEGRATIONS_VESPA_REC } from '../../../../constants'
import { BLUECONIC_DISPLAY_VALUE } from './blueconic/constants'
import { PUSHLY_AI_CURATOR_DISPLAY_VALUE } from './vespa/constants'

export type RecommendationSourceOption = { label: string; value: RecommendationSource }

/**
 * UI-level "content source" value for the unified feed/recommendation step editor.
 *
 * Feed is intentionally NOT a member of the models `RecommendationSource` enum —
 * a feed step is its own action type (`SEND_FEED_NOTIFICATION`) with its own
 * params. The editor unifies the *selector* only, so it models the feed option
 * as a UI-level string that lives alongside the two real recommendation sources.
 */
export const FEED_CONTENT_SOURCE = 'feed'

export type ContentSource = RecommendationSource.BLUECONIC | RecommendationSource.VESPA | typeof FEED_CONTENT_SOURCE

/** Display label for the Feed content-source option. */
export const FEED_SOURCE_DISPLAY_VALUE = 'Feed'

export type ContentSourceOption = { label: string; value: ContentSource }

/**
 * Source options for the unified content-source select. Extends the
 * recommendation sources ({@link getRecommendationSourceOptions} — BlueConic
 * always, Vespa gated by FEAT_INTEGRATIONS_VESPA_REC) with a Feed option that
 * is always offered.
 *
 * Order matches the add-step precedence: BlueConic, Vespa, Feed.
 *
 * NOTE: this only controls which options are *offered* for a fresh selection.
 * The editor separately guarantees the currently-selected source is always
 * present (so an existing feed/Vespa step still shows its own source even on a
 * domain whose add-step flag is off).
 */
export function getContentSourceOptions(domainFlags: string[] | undefined): ContentSourceOption[] {
    // getRecommendationSourceOptions only ever returns BlueConic/Vespa values,
    // both of which are members of ContentSource — widen the option type.
    const options: ContentSourceOption[] = getRecommendationSourceOptions(domainFlags).map((o) => ({
        label: o.label,
        value: o.value as ContentSource,
    }))

    options.push({ label: FEED_SOURCE_DISPLAY_VALUE, value: FEED_CONTENT_SOURCE })

    return options
}

/**
 * Source options for the recommendation-source select. BlueConic is always
 * available; Pushly AI Curator is gated behind the FEAT_INTEGRATIONS_VESPA_REC
 * domain flag so it stays hidden until the integration is enabled for a client,
 * matching the backend domain gating (see submarine-communications-cable's
 * Vespa bundle).
 *
 * Returns plain data (no JSX) so the flag-gating logic stays pure and unit
 * testable. The Select renders these labels as plain text; the Pushly AI brand
 * mark lives on the canvas node subtitle instead (see step-config.tsx), so the
 * dropdown stays a straight list of names.
 */
export function getRecommendationSourceOptions(domainFlags: string[] | undefined): RecommendationSourceOption[] {
    const options: RecommendationSourceOption[] = [
        { label: BLUECONIC_DISPLAY_VALUE, value: RecommendationSource.BLUECONIC },
    ]

    if (domainFlags?.includes(FEAT_INTEGRATIONS_VESPA_REC)) {
        options.push({ label: PUSHLY_AI_CURATOR_DISPLAY_VALUE, value: RecommendationSource.VESPA })
    }

    return options
}

export const validateRecommendedNotification = (params: AbstractRecommendedNotificationParams): [boolean, string] => {
    try {
        params.validate()
    } catch (error) {
        if (error instanceof InvalidFieldException || error instanceof MissingFieldException) {
            const displayError = extractFriendlyErrorMessage(error, {
                fieldNamesMap: {
                    recommendation_source: 'Recommendation Source',
                    recommendation_config: 'Recommendation Configuration',
                    'recommendation_config.*.boosts': 'Recommendation Configuration boost',
                    'recommendation_config.*.boosts.*.value': 'Boost value',
                    'recommendation_config.*.boosts.*.rampUp': 'Boost ramp up',
                    'recommendation_config.*.boosts.*.algorithm': 'Boost algorithm',
                    'recommendation_config.*.filters': 'Filters',
                },
                startWithProperty: true,
            })

            return [false, displayError.message]
        }

        return [false, error instanceof Error ? error.message : 'Validation failed']
    }

    // Content Window (freshness_max_age) is a required field in the Vespa UI.
    // The model marks it optional (legacy configs predate the control), so the
    // required-field contract is enforced here rather than letting an empty
    // Content Window input save a null value.
    if (isVespaRecommendedNotificationParams(params)) {
        const missingWindow = (params.recommendation_config ?? []).some((config) => config.freshness_max_age == null)
        if (missingWindow) {
            return [false, 'Content Window is required for every configuration.']
        }
    }

    return [true, '']
}
